import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { LocksResponse } from '@/lib/collections/locks';
import { updateLocks } from '@/lib/collections/locks';
import {
  getCountdown,
  formatCountdown,
  getProgress,
  formatUnlockDate,
} from '@/utils/lockHelpers';
import {
  getTokenByMint,
  formatTokenAmount,
} from '@/utils/tokenConfig';
import { EarlyUnlockDialog } from '@/components/EarlyUnlockDialog';
import { Lock, Unlock, AlertTriangle, Skull, Link } from 'lucide-react';

interface LockCardProps {
  lockId: string;
  lock: LocksResponse;
  compact?: boolean;
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'active') {
    return (
      <Badge
        className="text-xs px-2 py-0.5"
        style={{
          background: 'hsl(38 65% 57% / 0.15)',
          color: 'hsl(43 75% 68%)',
          border: '1px solid hsl(38 65% 57% / 0.3)',
        }}
      >
        Active
      </Badge>
    );
  }
  if (status === 'unlocked') {
    return (
      <Badge
        className="text-xs px-2 py-0.5"
        style={{
          background: 'hsl(142 60% 35% / 0.15)',
          color: 'hsl(142 60% 55%)',
          border: '1px solid hsl(142 60% 35% / 0.3)',
        }}
      >
        Unlocked
      </Badge>
    );
  }
  return (
    <Badge
      className="text-xs px-2 py-0.5"
      style={{
        background: 'hsl(0 65% 48% / 0.15)',
        color: 'hsl(0 65% 60%)',
        border: '1px solid hsl(0 65% 48% / 0.3)',
      }}
    >
      Early Exit
    </Badge>
  );
}

function LockTypeBadge({ lockType }: { lockType?: string }) {
  if (lockType === 'perma') {
    return (
      <Badge
        className="text-xs px-2 py-0.5 flex items-center gap-1"
        style={{
          background: 'hsl(0 72% 40% / 0.2)',
          color: 'hsl(0 72% 72%)',
          border: '1px solid hsl(0 72% 50% / 0.45)',
          fontWeight: 700,
          letterSpacing: '0.04em',
        }}
      >
        <Skull size={10} />
        PERMALOCK
      </Badge>
    );
  }
  return (
    <Badge
      className="text-xs px-2 py-0.5 flex items-center gap-1"
      style={{
        background: 'hsl(35 8% 52% / 0.1)',
        color: 'hsl(35 8% 55%)',
        border: '1px solid hsl(35 12% 20%)',
      }}
    >
      <Lock size={9} />
      STANDARD
    </Badge>
  );
}

function TokenBadge({ tokenMint }: { tokenMint?: string }) {
  const token = getTokenByMint(tokenMint);
  return (
    <Badge
      className="text-xs px-2 py-0.5 font-bold tracking-wide"
      style={{
        background: token.brandColorBg,
        color: token.brandColor,
        border: `1px solid ${token.brandColorBorder}`,
      }}
    >
      {token.symbol}
    </Badge>
  );
}

interface LockProgressBarProps {
  progress: number;
  isNearComplete: boolean;
  progressBarStyle: React.CSSProperties;
  progressBarColor: string;
  completedLabel?: string;
}

function LockProgressBar({
  progress,
  isNearComplete,
  progressBarStyle,
  progressBarColor,
  completedLabel,
}: LockProgressBarProps) {
  return (
    <div>
      {/* Track */}
      <div
        className="relative h-1.5 w-full overflow-hidden rounded-full"
        style={{ background: 'hsl(35 12% 12%)' }}
      >
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${progress}%`, ...progressBarStyle }}
        />
        {/* Glow pulse overlay at ≥90% */}
        {isNearComplete && progress < 100 && (
          <div
            className="absolute inset-y-0 right-0 w-8 opacity-60"
            style={{
              background: `linear-gradient(90deg, transparent, ${progressBarColor})`,
              animation: 'pulse 1.8s ease-in-out infinite',
            }}
          />
        )}
      </div>
      {/* Labels */}
      <div
        className="flex justify-between text-xs mt-1.5"
        style={{ color: 'hsl(35 8% 40%)' }}
      >
        <span>Created</span>
        <span
          className="font-medium tabular-nums"
          style={{ color: progressBarColor }}
        >
          {completedLabel ? `${completedLabel} · ` : ''}{progress.toFixed(0)}% complete
          {isNearComplete && progress < 100 && (
            <span
              className="ml-1.5 text-xs"
              style={{ color: progressBarColor, opacity: 0.75 }}
            >
              ✦ Points eligible
            </span>
          )}
        </span>
        <span>Unlocks</span>
      </div>
    </div>
  );
}

export const LockCard: React.FC<LockCardProps> = ({ lockId, lock, compact = false }) => {
  const [countdown, setCountdown] = useState(() => getCountdown(lock.unlockAt));
  const [progress, setProgress] = useState(() => getProgress(lock.ts, lock.unlockAt));
  const [earlyUnlockOpen, setEarlyUnlockOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const isPerma = lock.lockType === 'perma';

  useEffect(() => {
    if (lock.status !== 'active') return;
    const interval = setInterval(() => {
      setCountdown(getCountdown(lock.unlockAt));
      setProgress(getProgress(lock.ts, lock.unlockAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [lock.unlockAt, lock.ts, lock.status]);

  const canUnlock = countdown.expired && lock.status === 'active';
  const isActive = lock.status === 'active';

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      const success = await updateLocks(lockId, { status: 'unlocked' });
      if (success) {
        toast.success('Vault unlocked!', {
          description: `${formatTokenAmount(lock.lkAmt, lock.tokenMint)} returned to your wallet.`,
        });
      } else {
        toast.error('Unlock failed', { description: 'Transaction denied by the vault.' });
      }
    } catch (e) {
      toast.error('Something went wrong', { description: String(e) });
    } finally {
      setUnlocking(false);
    }
  };

  const token = getTokenByMint(lock.tokenMint);

  const isNearComplete = progress >= 90;
  const isCompleted = lock.status !== 'active';

  // Progress bar color logic
  const progressBarStyle = (() => {
    if (isCompleted) {
      return { background: 'hsl(142 60% 45%)' };
    }
    if (isNearComplete) {
      // ≥90% — gold glow to signal early-unlock point retention threshold
      return {
        background: `linear-gradient(90deg, ${token.brandColorMuted}, ${token.brandColor})`,
        boxShadow: `0 0 8px ${token.brandColor}80`,
      };
    }
    if (isPerma) {
      return { background: 'linear-gradient(90deg, hsl(0 60% 38%), hsl(0 65% 52%))' };
    }
    return { background: `linear-gradient(90deg, ${token.brandColorMuted}, ${token.brandColor})` };
  })();

  const progressBarColor = isCompleted
    ? 'hsl(142 60% 55%)'
    : isNearComplete
    ? token.brandColor
    : 'hsl(35 8% 40%)';

  if (compact) {
    return (
      <div
        className="rounded-lg overflow-hidden"
        style={{
          background: 'hsl(30 6% 7%)',
          border: isPerma
            ? '1px solid hsl(0 65% 40% / 0.3)'
            : '1px solid hsl(35 12% 14%)',
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-y-2 px-4 py-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {isPerma ? (
              <Skull size={14} className="shrink-0" style={{ color: 'hsl(0 65% 55%)' }} />
            ) : (
              <Lock size={14} className="shrink-0" style={{ color: token.brandColorMuted }} />
            )}
            <div className="min-w-0">
              <span className="text-sm font-medium" style={{ color: token.brandColor }}>
                {formatTokenAmount(lock.lkAmt, lock.tokenMint)}
              </span>
              <span className="text-xs ml-2 whitespace-nowrap" style={{ color: 'hsl(35 8% 52%)' }}>
                until {formatUnlockDate(lock.unlockAt)}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <span
              className="text-xs tabular-nums whitespace-nowrap"
              style={{ color: progressBarColor, minWidth: '4.5rem', textAlign: 'right' }}
            >
              {progress.toFixed(0)}% complete
            </span>
            <TokenBadge tokenMint={lock.tokenMint} />
            <LockTypeBadge lockType={lock.lockType} />
            <StatusBadge status={lock.status} />
          </div>
        </div>
        {/* Slim progress bar at the bottom of the compact row */}
        <div className="h-0.5 w-full" style={{ background: 'hsl(35 12% 10%)' }}>
          <div
            className="h-full transition-all duration-300"
            style={{ width: `${progress}%`, ...progressBarStyle }}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="relative rounded-xl overflow-hidden group transition-all duration-300"
        style={{
          background: isPerma
            ? 'linear-gradient(135deg, hsl(0 8% 7%) 0%, hsl(0 6% 9%) 100%)'
            : 'linear-gradient(135deg, hsl(30 6% 7%) 0%, hsl(30 6% 8%) 100%)',
          border: isActive
            ? isPerma
              ? '1px solid hsl(0 65% 42% / 0.35)'
              : `1px solid ${token.brandColorBorder}`
            : '1px solid hsl(35 12% 14%)',
        }}
      >
        {/* Top accent line */}
        {isActive && (
          <div
            className="absolute top-0 left-0 right-0 h-px"
            style={{
              background: isPerma
                ? 'linear-gradient(90deg, transparent, hsl(0 65% 50% / 0.7), transparent)'
                : `linear-gradient(90deg, transparent, ${token.brandColor}, transparent)`,
            }}
          />
        )}

        {/* PermaLOCK chain watermark */}
        {isPerma && (
          <div
            className="absolute top-4 right-4 opacity-5 group-hover:opacity-10 transition-opacity duration-500"
          >
            <Link size={64} style={{ color: 'hsl(0 65% 55%)' }} />
          </div>
        )}

        <div className="p-6">
          {/* Header row */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <div
                className="text-3xl font-semibold mb-1"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: isPerma ? 'hsl(0 55% 68%)' : token.brandColor,
                }}
              >
                {formatTokenAmount(lock.lkAmt, lock.tokenMint)}
              </div>
              <div className="text-xs" style={{ color: 'hsl(35 8% 52%)' }}>
                deposited {formatTokenAmount(lock.depAmt, lock.tokenMint)} · unlocks {formatUnlockDate(lock.unlockAt)}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <TokenBadge tokenMint={lock.tokenMint} />
              <LockTypeBadge lockType={lock.lockType} />
              <StatusBadge status={lock.status} />
            </div>
          </div>

          {/* Countdown + progress */}
          {isActive && (
            <div className="mb-5">
              <div
                className="text-lg font-medium tracking-wider mb-2"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: countdown.expired
                    ? 'hsl(142 60% 55%)'
                    : isPerma
                    ? 'hsl(0 40% 80%)'
                    : 'hsl(40 15% 85%)',
                }}
              >
                {formatCountdown(countdown)}
              </div>
              <LockProgressBar
                progress={progress}
                isNearComplete={isNearComplete}
                progressBarStyle={progressBarStyle}
                progressBarColor={progressBarColor}
              />
            </div>
          )}

          {/* Progress for completed / early-exited locks */}
          {!isActive && (
            <div className="mb-5">
              <LockProgressBar
                progress={progress}
                isNearComplete={isNearComplete}
                progressBarStyle={progressBarStyle}
                progressBarColor={progressBarColor}
                completedLabel={
                  lock.status === 'earlyUnlocked'
                    ? 'Early exit'
                    : 'Completed'
                }
              />
            </div>
          )}

          {/* Action buttons */}
          {isActive && (
            <div className="flex gap-3">
              {/* Normal unlock — shown for both types when timer expired */}
              <Button
                size="sm"
                disabled={!canUnlock || unlocking}
                onClick={handleUnlock}
                className="text-xs font-medium"
                style={{
                  flex: isPerma ? '1' : undefined,
                  ...(canUnlock
                    ? {
                        background: isPerma
                          ? 'linear-gradient(135deg, hsl(0 60% 38%), hsl(0 65% 50%))'
                          : `linear-gradient(135deg, ${token.brandColorMuted}, ${token.brandColor})`,
                        color: 'hsl(30 8% 4%)',
                        border: 'none',
                      }
                    : {
                        background: 'hsl(30 5% 12%)',
                        color: 'hsl(35 8% 40%)',
                        border: '1px solid hsl(35 12% 16%)',
                        cursor: 'not-allowed',
                        flex: isPerma ? '1' : undefined,
                      }),
                }}
              >
                <Unlock size={12} className="mr-1.5" />
                {unlocking ? 'Unlocking...' : canUnlock ? 'Unlock' : 'Locked'}
              </Button>

              {/* Early unlock — only for standard locks */}
              {!isPerma && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEarlyUnlockOpen(true)}
                  className="flex-1 text-xs"
                  style={{
                    color: 'hsl(0 65% 55%)',
                    border: '1px solid hsl(0 65% 48% / 0.3)',
                    background: 'hsl(0 65% 48% / 0.05)',
                  }}
                >
                  <AlertTriangle size={12} className="mr-1.5" />
                  Early Unlock
                </Button>
              )}

              {/* PermaLOCK — sealed label when timer not expired */}
              {isPerma && !canUnlock && (
                <div
                  className="flex items-center justify-center gap-1.5 px-3 rounded-md text-xs"
                  style={{
                    background: 'hsl(0 60% 25% / 0.15)',
                    border: '1px solid hsl(0 60% 40% / 0.2)',
                    color: 'hsl(0 50% 55%)',
                  }}
                >
                  <Skull size={11} />
                  No early exit
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* EarlyUnlockDialog only mounts for standard locks */}
      {!isPerma && (
        <EarlyUnlockDialog
          lock={lock}
          lockId={lockId}
          open={earlyUnlockOpen}
          onClose={() => setEarlyUnlockOpen(false)}
        />
      )}
    </>
  );
};

export default LockCard;
